import { Timestamp } from "firebase/firestore";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import useCreateTeamNotification from "../notifications/useCreateTeamNotification";
import { useNotificationsContext } from "@/app/_providers/NotificationsProvider/NotificationsProvider";
import { authenticatedFetch } from "@/app/_utils/authenticatedFetch";

const fetchSubscription = async (uid: string): Promise<Subscription> => {
  const response = await authenticatedFetch(`/api/subscription`);
  if (!response.ok) {
    throw new Error("Failed to fetch subscription status");
  }
  const data = await response.json();

  return {
    ...data,
    createdAt: new Timestamp(
      data.createdAt._seconds,
      data.createdAt._nanoseconds
    ),
  };
};

export const useGetSubscription = (uid: string | undefined) => {
  const { createTeamNotification } = useCreateTeamNotification();
  const { notifications } = useNotificationsContext();

  const {
    data,
    error,
    isPending: isLoading,
    isError,
    refetch,
  } = useQuery<Subscription, Error>({
    queryKey: ["subscription", uid],
    queryFn: () => fetchSubscription(uid!),
    enabled: !!uid,
    staleTime: 3600000, // 1 hour
    gcTime: 3600000, // 1 hour
  });

  useEffect(() => {
    // we always have notifications in db, so 0 means data is not loaded yet
    if (data && notifications && notifications.length > 0) {
      const usagePercentage = (data.smsCount / data.smsLimit) * 100;

      // Calculate days since subscription started
      const currentDate = Timestamp.now();
      const subscriptionDate = data.createdAt;
      const daysSinceSubscription = Math.floor(
        (currentDate.toMillis() - subscriptionDate.toMillis()) /
          (1000 * 60 * 60 * 24)
      );

      // Calculate which 30-day period we're in (0-based)
      const billingPeriod = Math.floor(daysSinceSubscription / 30);

      // Create a unique ID for the current 30-day period
      const billingPeriodId = `${subscriptionDate
        .toDate()
        .getFullYear()}-${billingPeriod}`;

      // Check if notification already exists for current billing period
      const hasExistingNotification = (notificationId: string) => {
        return notifications.some(
          (notification) => notification.id === notificationId
        );
      };

      if (data.smsCount >= data.smsLimit) {
        const notificationId = `SMS-100-${billingPeriodId}`;
        if (!hasExistingNotification(notificationId)) {
          createTeamNotification({
            id: notificationId,
            title: "SMS Usage Critical",
            body: "You have used all of your SMS limit. Please contact support to increase your limit.",
          });
        }
      } else if (usagePercentage >= 75) {
        const level = usagePercentage >= 90 ? "90" : "75";
        const warningType = usagePercentage >= 90 ? "Critical" : "Warning";
        const notificationId = `SMS-${level}-${billingPeriodId}`;

        if (!hasExistingNotification(notificationId)) {
          createTeamNotification({
            id: notificationId,
            title: `SMS Usage ${warningType}`,
            body: `You are using over ${level}% of your SMS limit, you have ${
              data.smsLimit - data.smsCount
            } SMS left.`,
          });
        }
      }
    }
  }, [data, notifications]);

  return { data, error, isLoading, isError, refetch };
};
